import i18next from 'i18next';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  trackOptimizelyCustomEvent,
  trackRegistrationEvent,
} from '../../utils/tracker';
import {
  DisabledButton,
  RawFormLabel,
  SubmitButton,
  RadioCard,
  RadioCardClassNamesProps,
} from '../common/form';
import {
  withUnloadConfirm,
  detachUnloadHandler,
} from '../common/composer/withUnloadConfirm';
import {
  gradeOptions,
  categorizedGradeOptions,
  getCategoryGradeLabel,
  gradeCategories,
  GradeOption,
  GradeCategory,
  findGradeCategory,
  isJunior,
} from '.';

const GradeForm: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [gradeId, setGradeId] = React.useState('');
  const [gradeCode, setGradeCode] = React.useState('');
  const [gradeLabel, setGradeLabel] = React.useState('');
  const [selectedCategory, setSelectedCategory] = React.useState<
    GradeCategory | undefined
  >(undefined);
  const [shouldAutoNavigate, setShouldAutoNavigate] = React.useState(false);

  const setGrade = (gradeOption: GradeOption | undefined) => {
    setGradeId(gradeOption?.value || '');
    setGradeCode(gradeOption?.code || '');
    setGradeLabel(gradeOption?.label || '');
  };

  const gradeLabelCardClassNames: RadioCardClassNamesProps = {
    radioCard: 'grade-radio-card',
  };

  const sendGradeSelectionEvents = React.useCallback(
    (selectedGradeCode: string) => {
      trackRegistrationEvent(1, selectedGradeCode);
      trackOptimizelyCustomEvent('grade_selection_completed');
    },
    [],
  );

  const navigateToNext = React.useCallback(() => {
    // darklaunch の値に応じて未就学, 小学生, 中学生 は 新EF に遷移させる
    if (
      document.getElementById('root')?.dataset.enableNewEntryForm === 'true' &&
      isJunior(gradeId)
    ) {
      // ページ離脱の確認を省略する
      detachUnloadHandler();
      window.location.href = `${document.getElementById('root')?.dataset.manageWebUrl}/register/grade/${gradeCode}/`;
    } else {
      navigate(
        { pathname: '/learner' },
        { state: { gradeId, gradeCode, gradeLabel } },
      );
    }
  }, [navigate, gradeId, gradeCode, gradeLabel]);

  // grade_code parameter が付いた状態でページに着地したら
  // 自動で学年を選択した状態にして次のページへ遷移
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const grade_code = params.get('grade_code');
    if (!grade_code) return;

    const selectedOption = gradeOptions.find(
      (option) => option.code === grade_code,
    );
    if (!selectedOption) return;

    setShouldAutoNavigate(true);
    setGrade(selectedOption);
    setSelectedCategory(findGradeCategory(selectedOption));
    sendGradeSelectionEvents(selectedOption.code);
  }, [location.search, sendGradeSelectionEvents]);

  React.useEffect(() => {
    if (shouldAutoNavigate && gradeId && selectedCategory) {
      navigateToNext();
    }
  }, [shouldAutoNavigate, gradeId, selectedCategory, navigateToNext]);

  const handleSubmit = React.useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      sendGradeSelectionEvents(gradeCode);
      navigateToNext();
    },
    [sendGradeSelectionEvents, navigateToNext, gradeCode],
  );

  return (
    <form onSubmit={handleSubmit} data-test-form-id="register-grade">
      <div className="grade-form-title">
        {i18next.t('registration.sign_up.new.register_learner_grade.title')}
      </div>
      <div className="row sm-up-m-b-xl xs-down-m-b-lg">
        <div className="col-sm-10 col-sm-offset-1 col-lg-8 col-lg-offset-2 text-center">
          {i18next.t('registration.sign_up.new.register_learner_grade.lead')}
        </div>
      </div>
      <div>
        <div className="row m-t-md">
          <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3">
            <RawFormLabel
              label={i18next.t(
                'registration.sign_up.new.register_learner_grade.category_label',
              )}
              requiredValues={selectedCategory}
            />
          </div>
          <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3 grade-radio-cards">
            {gradeCategories.map((category) => (
              <RadioCard
                key={category}
                value={category}
                selectedValue={selectedCategory}
                label={i18next.t(
                  `registration.sign_up.new.register_learner_grade.category.${category}`,
                )}
                fieldName="category"
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setGrade(undefined);
                }}
                classNames={gradeLabelCardClassNames}
              />
            ))}
          </div>
        </div>
        {selectedCategory && (
          <div className="row m-t-lg">
            <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3">
              <RawFormLabel
                label={i18next.t(
                  'registration.sign_up.new.register_learner_grade.category_grade_label',
                )}
                requiredValues={gradeId}
              />
            </div>
            <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3 grade-radio-cards">
              {categorizedGradeOptions[selectedCategory]?.map((gradeOption) => {
                const labels = getCategoryGradeLabel(gradeOption.label);

                return (
                  <RadioCard
                    key={gradeOption.value}
                    value={gradeOption.value}
                    selectedValue={gradeId}
                    label={labels.primary}
                    secondaryLabel={labels.secondary}
                    fieldName={`${selectedCategory}_category_grade`}
                    onChange={(e) => {
                      const selectedOption = gradeOptions.find(
                        (option) => option.value === e.target.value,
                      );
                      setGrade(selectedOption);
                    }}
                    classNames={gradeLabelCardClassNames}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
      <div className="row m-t-lg">
        <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3 gray-dark size-sm">
          <div className="area m-b-0 grade-form-caution">
            <div className="m-b-md grade-form-caution-guide">
              <span>
                <svg className="icon text-success m-r-sm m-t-sm grade-form-caution-guide-tick">
                  <use xlinkHref="#common-tick" />
                </svg>
              </span>
              <span className="m-l-sm text-sapuri-blue grade-form-caution-guide-text">
                スタディサプリでは学年に関わらず全ての学年の動画を視聴することができます。
              </span>
            </div>
            <div>
              <span className="pink grade-form-caution-alert-text">
                本来の学年向けの機能が使えなくなる可能性がありますので、正しい学年を選択してください。
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="row sm-up-m-t-lg xs-down-m-t-lg sm-up-m-b-lg xs-down-m-b">
        <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3 col-md-6 col-md-offset-3">
          {gradeId ? (
            <SubmitButton
              isSubmitting={false}
              label={i18next.t(
                'registration.sign_up.new.register_learner_grade.submit',
              )}
              buttonClassNames="ytm-new_start"
            />
          ) : (
            <DisabledButton
              label={i18next.t(
                'registration.sign_up.new.register_learner_grade.not_ready_to_submit',
              )}
              buttonClassNames="p-y-md"
            />
          )}
        </div>
      </div>
    </form>
  );
};

export default withUnloadConfirm(GradeForm);
